import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { MODULE_PAGES, PAGE_LABELS, ACTION_PAGE, applyAccess, canAccessWith, indexGrants, principalsFor, parseRoles, sowBandKey, sowBandLabel } from '../../utils/rbac';
// One label map, shared with the account menu in the header, so a role cannot be called
// two different things in two places.
import { ROLE_LABELS } from '../../utils/identity';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { useToast } from '../ui/Toast';
import { ACCESS_TOUR_ID, accessTourSteps } from './accessTour';
import Avatar from '../shared/Avatar';

// Admin → Access (phase 2 of the team-based access design). One table,
// access_grants, keyed by principal: a team, a role or a person. This screen
// shows the table three ways and edits it in place:
//   Teams   the bulk of access: what every member of a team reaches, and who
//           is in the team (editable by admin, HR, or the team's own lead)
//   Roles   the narrower supplements, one column per role
//   People  one person: everything they reach and why, plus direct grants as
//           the exception path
// A tick is a row and an untick is its absence. Removing a page from a team or
// role shows who would lose it before anything is saved, and offers a direct
// grant to whoever still needs it. Page sets are admin only; the server says
// so through the overview, and this screen hides what the person may not do.

const MODULE_LABELS = {
  'business-dev': 'Bids',
  'commercial':   'Commercial',
  'delivery':     'Delivery',
  'procurement':  'Procurement',
  'hardware':     'Hardware',
  'people':       'People',
  'finance':      'Finance',
  'admin':        'Admin',
  'api':          'API access',
};

// The role columns: every labelled role. 'team-lead' is derived (teams.lead_id)
// and cannot be assigned, but its page set is a row like any other.
const ROLE_KEYS = Object.keys(ROLE_LABELS);
const ASSIGNABLE = ROLE_KEYS.filter(r => r !== 'team-lead' && r !== 'client');

const pkey = (type, key) => `${type}:${key}`;
const pageLabel = (page) => PAGE_LABELS[page] || page;
const isAction = (page) => page.includes(':');
const fullName = (p) => `${p.preferred_name || p.first_name || ''} ${p.last_name || ''}`.trim();

function indexFromRows(rows) {
  return indexGrants(rows);
}
function cloneIndex(idx) {
  return Object.fromEntries(Object.entries(idx).map(([k, v]) => [k, new Set(v)]));
}
function sameSet(a = new Set(), b = new Set()) {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

function AvatarChip({ person, onRemove, removeTitle, note }) {
  const initials = [person.preferred_name || person.first_name, person.last_name]
    .filter(Boolean).map(s => s[0].toUpperCase()).join('');
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-medium"
      style={{ backgroundColor: person.avatar_color ? `${person.avatar_color}22` : '#e5e7eb', color: person.avatar_color || '#374151' }}
      title={note || fullName(person)}
    >
      <Avatar as="span" personId={person.id}
        className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-[7px] font-bold text-white flex-shrink-0"
        style={{ backgroundColor: person.avatar_color || '#6b7280' }}
      >{initials}</Avatar>
      {fullName(person)}
      {note && <span className="text-[7px] opacity-70">({note})</span>}
      {onRemove && (
        <button onClick={onRemove} title={removeTitle} aria-label={removeTitle} className="ml-0.5 text-[9px] leading-none hover:text-red-600">×</button>
      )}
    </span>
  );
}

export default function AccessPage() {
  const { toast } = useToast();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('teams'); // 'teams' | 'roles' | 'people'

  // The table as saved, and the working copy the checkboxes edit, both keyed
  // 'type:key' → Set<page>.
  const [saved, setSaved] = useState({});
  const [pending, setPending] = useState({});
  const [saving, setSaving] = useState(null);

  // Detail views: a team or role (type:key) or a person id.
  const [detail, setDetail] = useState(null);
  const [personId, setPersonId] = useState(null);
  const [personSearch, setPersonSearch] = useState('');
  const [addMember, setAddMember] = useState('');
  const [roleBusy, setRoleBusy] = useState(false);

  // The impact preview: { principal, pages, losers: [{ person, page, grantDirect }] }
  const [preview, setPreview] = useState(null);

  const tour = useFeatureTour(ACCESS_TOUR_ID, { enabled: !loading && !loadError });

  const load = useCallback(async () => {
    try {
      const o = await api.get('access/overview');
      setOverview(o);
      const idx = indexFromRows(o?.grants || []);
      setSaved(idx);
      setPending(cloneIndex(idx));
      setLoadError('');
    } catch (e) {
      setLoadError(e?.data?.error || 'The access overview could not be loaded.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const canEdit = !!overview?.can_edit_grants;
  const teams = overview?.teams || [];
  const people = overview?.people || [];
  const ledTeamIds = overview?.led_team_ids || [];
  const canManageTeam = (teamId) => !!overview?.can_manage_any_team || ledTeamIds.includes(teamId);

  // SOW value bands are rows an administrator adds, so their keys come with
  // the overview rather than from MODULE_PAGES: one row per active band under
  // Commercial, named by its threshold. They have no ACTION_PAGE parent, so a
  // band is tickable on its own. Who may approve a SOW's issue is decided here.
  const modulePages = useMemo(() => ({
    ...MODULE_PAGES,
    commercial: [...MODULE_PAGES.commercial, ...(overview?.bands || []).map(b => sowBandKey(b.id))],
  }), [overview]);
  const bandLabels = useMemo(() => Object.fromEntries((overview?.bands || []).map(b => [sowBandKey(b.id), sowBandLabel(b)])), [overview]);
  const labelOf = (page) => bandLabels[page] || pageLabel(page);
  const byLabel = (a, b) => String(labelOf(a)).localeCompare(String(labelOf(b)), 'en-GB', { sensitivity: 'base' });

  // Who acts as what. A team lead acts as the derived team-lead role, so the
  // reason column can say so.
  const leadsOf = useMemo(() => new Set(teams.map(t => t.lead_id).filter(Boolean)), [teams]);
  const principalsOf = useCallback((p) => {
    const out = principalsFor(p, (p.extra_memberships || []).map(m => m.team_id));
    if (leadsOf.has(p.id)) out.splice(out.findIndex(x => !x.startsWith('role:')) === -1 ? out.length : out.findIndex(x => !x.startsWith('role:')), 0, 'role:team-lead');
    return out;
  }, [leadsOf]);

  const membersOf = useCallback((teamId) => people.filter(p => p.team_id === teamId || (p.extra_memberships || []).some(m => m.team_id === teamId)), [people]);
  const holdersOf = useCallback((roleKey) => people.filter(p => roleKey === 'team-lead' ? leadsOf.has(p.id) : parseRoles(p.user_role).includes(roleKey)), [people, leadsOf]);
  const peopleActingAs = (type, key) => type === 'team' ? membersOf(key) : type === 'role' ? holdersOf(key) : people.filter(p => p.id === key);

  const teamName = (id) => teams.find(t => t.id === id)?.name || id;
  const principalLabel = (principal) => {
    const [type, key] = [principal.slice(0, principal.indexOf(':')), principal.slice(principal.indexOf(':') + 1)];
    if (type === 'team') return `via team ${teamName(key)}`;
    if (type === 'role') return key === 'admin' ? 'administrator' : `via role ${ROLE_LABELS[key] || key}`;
    return 'direct grant';
  };

  // Why does this person reach this page? Every principal of theirs that holds it.
  const reasonsFor = (person, page, grants = pending) => {
    const ps = principalsOf(person);
    if (ps.includes('role:admin')) return ['administrator'];
    const out = ps.filter(p => grants[p]?.has(page)).map(principalLabel);
    if (!out.length && page === 'my-bookings' && ps.some(p => p.startsWith('role:') && p !== 'role:client')) out.push('every member of staff');
    return out;
  };

  // ── Editing a principal's page set ──────────────────────────────────────────
  const held = (principal, page) => pending[principal]?.has(page) ?? false;
  const dirty = (principal) => !sameSet(saved[principal], pending[principal]);
  const toggle = (principal, page) => {
    if (!canEdit) return;
    setPending(prev => {
      const next = { ...prev };
      const set = new Set(next[principal] || []);
      if (set.has(page)) {
        set.delete(page);
        // An action cannot outlive its page.
        for (const [action, parent] of Object.entries(ACTION_PAGE)) if (parent === page) set.delete(action);
      } else {
        set.add(page);
      }
      next[principal] = set;
      return next;
    });
  };
  const reset = (principal) => setPending(prev => ({ ...prev, [principal]: new Set(saved[principal] || []) }));

  // Save, but first: would anybody lose a page they still need? Somebody acting
  // as this principal who reaches the page through no other principal.
  const requestSave = (principal) => {
    const [type, key] = [principal.slice(0, principal.indexOf(':')), principal.slice(principal.indexOf(':') + 1)];
    const removed = [...(saved[principal] || [])].filter(p => !pending[principal]?.has(p));
    const losers = [];
    if (removed.length) {
      const after = { ...pending };
      for (const person of peopleActingAs(type, key)) {
        const ps = principalsOf(person);
        if (ps.includes('role:admin')) continue;
        for (const page of removed) {
          const stillHeld = ps.filter(p => p !== principal).some(p => after[p]?.has(page));
          if (!stillHeld) losers.push({ person, page, grantDirect: false });
        }
      }
    }
    if (losers.length) setPreview({ principal, losers });
    else commitSave(principal, []);
  };

  const commitSave = async (principal, directGrants) => {
    const [type, key] = [principal.slice(0, principal.indexOf(':')), principal.slice(principal.indexOf(':') + 1)];
    setSaving(principal);
    setPreview(null);
    try {
      const pages = [...(pending[principal] || [])];
      await api.post('access-grants', { principal_type: type, principal_key: key, pages });
      // Anyone the preview said to keep whole gets the page as a direct grant.
      const byPerson = {};
      for (const { person, page } of directGrants) (byPerson[person.id] ||= new Set(pending[pkey('person', person.id)] || [])).add(page);
      for (const [id, set] of Object.entries(byPerson)) {
        await api.post('access-grants', { principal_type: 'person', principal_key: id, pages: [...set] });
      }
      await load();
      // The administrator's own menu reads the same table.
      api.get('rbac/config').then(cfg => applyAccess({ principals: cfg?.principals || [], grants: cfg?.grants || [] })).catch(() => {});
      toast.success('Access saved.');
    } catch (e) {
      toast.error(e?.data?.error || 'Save failed.');
    } finally { setSaving(null); }
  };

  // ── Membership ──────────────────────────────────────────────────────────────
  const addToTeam = async (teamId, id) => {
    if (!id) return;
    try {
      await api.post('person-teams', { person_id: id, team_id: teamId });
      setAddMember('');
      await load();
      toast.success('Added to the team.');
    } catch (e) { toast.error(e?.data?.error || 'Could not add them.'); }
  };
  const removeFromTeam = async (membershipId) => {
    try {
      await api.delete(`person-teams/${membershipId}`);
      await load();
      toast.success('Removed from the team.');
    } catch (e) { toast.error(e?.data?.error || 'Could not remove them.'); }
  };

  // ── Roles on a person ───────────────────────────────────────────────────────
  const toggleRole = async (person, role) => {
    const current = parseRoles(person.user_role);
    const next = current.includes(role) ? current.filter(r => r !== role) : [...current, role];
    if (!next.length) { toast.error('A person needs at least one role.'); return; }
    setRoleBusy(true);
    try {
      await api.put(`people/${person.id}`, { user_role: next.join(',') });
      await load();
    } catch (e) { toast.error(e?.data?.error || 'Could not change the roles.'); }
    finally { setRoleBusy(false); }
  };

  // ── Rendering helpers ───────────────────────────────────────────────────────
  const pageRows = (predicate = () => true) => Object.entries(modulePages)
    .map(([mod, pages]) => [mod, pages.filter(predicate)])
    .filter(([, pages]) => pages.length);

  // Render helpers, not components: a component type created inside this
  // function is new on every render, so React would remount every checkbox
  // (and lose focus) each time the app polls. Plain functions keep the tree stable.
  const checkbox = (principal, page) => {
    const parent = ACTION_PAGE[page];
    const parentHeld = !parent || held(principal, parent);
    const active = held(principal, page);
    return (
      <input
        type="checkbox"
        checked={active}
        disabled={!canEdit || !parentHeld}
        onChange={() => toggle(principal, page)}
        className={`accent-navy ${canEdit && parentHeld ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
        title={!canEdit ? 'Only an administrator changes page sets' : !parentHeld ? `Needs ${labelOf(parent)} first` : `${active ? 'Remove' : 'Grant'} ${labelOf(page)}`}
      />
    );
  };

  const saveControls = (principal, small = false) => dirty(principal) ? (
    <div className={`flex items-center gap-1 ${small ? '' : 'ml-auto'}`}>
      {!small && <span className="text-[8px] text-amber-600 font-semibold">Unsaved changes</span>}
      <button onClick={() => reset(principal)} className={small ? 'text-[6px] text-gray-400 hover:text-gray-600' : 'text-[8px] text-gray-500 underline'} title="Discard the unsaved changes">{small ? '↺' : 'Reset'}</button>
      <button onClick={() => requestSave(principal)} disabled={saving === principal}
        className={small ? 'text-[6px] px-1 py-px bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50 font-semibold' : 'px-2.5 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50'}>
        {saving === principal ? '…' : small ? 'Save' : 'Save changes'}
      </button>
    </div>
  ) : null;

  // The matrix: one column per principal of a kind, one row per page. It
  // scrolls inside its own panel so the column names stay in view: a hundred
  // rows down, a tick is meaningless without its team.
  const matrix = (type, columns) => (
    <div data-tour="matrix" className="bg-white border border-gray-200 rounded overflow-auto max-h-[calc(100vh-240px)]">
      <table className="w-full" style={{ fontSize: 8 }}>
        <thead className="sticky top-0 z-20">
          <tr className="bg-gray-50">
            <th className="text-left px-3 py-2 font-bold text-gray-600 uppercase tracking-wide border-b border-gray-200 sticky left-0 z-30 bg-gray-50 min-w-[160px]">Page</th>
            {columns.map(c => (
              <th key={c.key} className="px-2 py-2 font-bold text-gray-500 uppercase border-b border-gray-200 text-center whitespace-nowrap bg-gray-50">
                <div className="flex flex-col items-center gap-0.5">
                  <button onClick={() => setDetail(pkey(type, c.key))} className="hover:text-blue-600 hover:underline">{c.label}</button>
                  {saveControls(pkey(type, c.key), true)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pageRows().map(([mod, pages]) => (
            <Fragment key={mod}>
              <tr className="bg-gray-50">
                <td colSpan={columns.length + 1} className="px-3 py-1 text-[7px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 sticky left-0 bg-gray-50">
                  {MODULE_LABELS[mod] || mod}
                </td>
              </tr>
              {pages.map(page => (
                <tr key={page} className="border-b border-gray-50 hover:bg-blue-50/40">
                  <td className={`px-3 py-1 text-gray-700 font-medium sticky left-0 bg-white ${isAction(page) ? 'pl-7 text-gray-500' : ''}`}>
                    {isAction(page) ? `↳ ${labelOf(page)}` : labelOf(page)}
                  </td>
                  {columns.map(c => (
                    <td key={c.key} className="px-2 py-1 text-center">{checkbox(pkey(type, c.key), page)}</td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Pages held by a principal, by module, with the rest offered underneath.
  const pageChecklist = (principal) => {
    const heldRows = pageRows(p => held(principal, p));
    const restRows = pageRows(p => !held(principal, p));
    return (
      <>
        {heldRows.length === 0 && <div className="mb-3 text-[9px] text-gray-500">Holds no pages{canEdit ? '. Grant some below.' : '.'}</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
          {heldRows.map(([mod, pages]) => (
            <div key={mod} className="bg-white border border-gray-200 rounded p-3">
              <div className="text-[8px] font-bold text-gray-500 uppercase tracking-wide mb-2">{MODULE_LABELS[mod] || mod}</div>
              <div className="space-y-1">
                {[...pages].sort(byLabel).map(p => (
                  <label key={p} className={`flex items-center gap-2 text-[9px] px-1 py-0.5 rounded ${isAction(p) ? 'pl-4' : ''} ${canEdit ? 'cursor-pointer hover:bg-gray-50' : ''}`}>
                    {checkbox(principal, p)}
                    <span className="text-gray-700">{labelOf(p)}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        {canEdit && restRows.length > 0 && (
          <details className="mb-3">
            <summary className="text-[8px] text-gray-500 cursor-pointer select-none font-semibold mb-2">+ Grant additional pages</summary>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
              {restRows.map(([mod, pages]) => (
                <div key={mod} className="bg-emerald-50 border border-emerald-200 rounded p-3">
                  <div className="text-[8px] font-bold text-emerald-700 uppercase tracking-wide mb-2">{MODULE_LABELS[mod] || mod}</div>
                  <div className="space-y-1">
                    {[...pages].sort(byLabel).map(p => (
                      <label key={p} className={`flex items-center gap-2 text-[9px] cursor-pointer hover:bg-emerald-100 px-1 py-0.5 rounded ${isAction(p) ? 'pl-4' : ''}`}>
                        {checkbox(principal, p)}
                        <span className="text-gray-600">{labelOf(p)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </>
    );
  };

  // ── The impact preview ──────────────────────────────────────────────────────
  const previewModal = () => {
    if (!preview) return null;
    const [type, key] = [preview.principal.slice(0, preview.principal.indexOf(':')), preview.principal.slice(preview.principal.indexOf(':') + 1)];
    const who = type === 'team' ? `team ${teamName(key)}` : `role ${ROLE_LABELS[key] || key}`;
    const flip = (i) => setPreview(prev => ({ ...prev, losers: prev.losers.map((l, j) => j === i ? { ...l, grantDirect: !l.grantDirect } : l) }));
    const kept = preview.losers.filter(l => l.grantDirect);
    return (
      <Modal title={`Removing pages from ${who}`} onClose={() => setPreview(null)} dataTour="impact-preview"
        footer={(
          <div className="flex items-center justify-between gap-2 w-full">
            <button onClick={() => setPreview(null)} className="px-3 py-1.5 text-[9px] font-semibold text-gray-700 bg-gray-100 rounded hover:bg-gray-200">Keep the pages</button>
            <button onClick={() => commitSave(preview.principal, kept)} disabled={saving === preview.principal}
              className="px-3 py-1.5 text-[9px] font-semibold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
              {kept.length ? `Remove, and grant directly to ${kept.length}` : 'Remove anyway'}
            </button>
          </div>
        )}>
        <p className="text-[9px] text-gray-700 mb-2">
          These people would lose a page they reach through nothing else. Tick anyone who still needs it and they keep it as a direct grant on their own record.
        </p>
        <div className="space-y-1">
          {preview.losers.map((l, i) => (
            <label key={`${l.person.id}:${l.page}`} className="flex items-center gap-2 text-[9px] px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={l.grantDirect} onChange={() => flip(i)} className="accent-navy" />
              <span className="font-medium text-gray-800">{fullName(l.person)}</span>
              <span className="text-gray-500">would lose</span>
              <span className="font-medium text-gray-800">{labelOf(l.page)}</span>
              <span className="ml-auto text-[8px] text-gray-400">{l.grantDirect ? 'Grant directly instead' : 'tick to grant directly'}</span>
            </label>
          ))}
        </div>
      </Modal>
    );
  };

  // ── Views ───────────────────────────────────────────────────────────────────
  if (loading) return <div className="fade-in lg:col-span-3 text-[9px] text-gray-400">Loading…</div>;
  if (loadError) return <div className="fade-in lg:col-span-3 text-[9px] text-red-600">{loadError}</div>;

  const header = (title, back) => (
    <div data-tour="page-header" className="flex items-center gap-3 mb-4 flex-wrap">
      {back && <button onClick={back} className="text-[9px] text-blue-600 underline">← Back</button>}
      <h2 className="text-base font-extrabold text-navy">{title}</h2>
      {!back && (
        <button onClick={tour.start} title="Replay the guided tour" aria-label="Replay the guided tour"
          className="w-4 h-4 rounded-full border border-gray-300 text-[9px] text-gray-500 hover:text-navy hover:border-navy leading-none">?</button>
      )}
    </div>
  );

  // A team or a role in detail.
  if (detail) {
    const [type, key] = [detail.slice(0, detail.indexOf(':')), detail.slice(detail.indexOf(':') + 1)];
    const team = type === 'team' ? teams.find(t => t.id === key) : null;
    const title = type === 'team' ? (team?.name || key) : (ROLE_LABELS[key] || key);
    const actors = peopleActingAs(type, key);
    const mayManage = type === 'team' && canManageTeam(key);
    const candidates = type === 'team' ? people.filter(p => !actors.some(a => a.id === p.id)) : [];
    return (
      <div className="fade-in lg:col-span-3" data-tour="team-detail">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button onClick={() => setDetail(null)} className="text-[9px] text-blue-600 underline">← Back to {type === 'team' ? 'teams' : 'roles'}</button>
          <h2 className="text-base font-extrabold text-navy">{title}</h2>
          <span className="text-[8px] text-gray-400 font-mono">{type}:{key}</span>
          {team?.lead_name && <span className="text-[8px] text-gray-500">Lead: {team.lead_name}</span>}
          {key === 'team-lead' && <span className="text-[8px] text-gray-500">Derived from each team's lead; it cannot be assigned.</span>}
          {saveControls(detail)}
        </div>

        <div className="mb-4 bg-white border border-gray-200 rounded p-3">
          <div className="text-[8px] font-bold text-gray-500 uppercase tracking-wide mb-2">
            {type === 'team' ? 'Members' : 'People with this role'} ({actors.length})
          </div>
          {actors.length === 0 ? (
            <span className="text-[8px] text-gray-400">Nobody active.</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {actors.map(p => {
                const extra = type === 'team' ? (p.extra_memberships || []).find(m => m.team_id === key) : null;
                const primary = type === 'team' && p.team_id === key;
                return (
                  <AvatarChip key={p.id} person={p}
                    note={primary ? 'primary team' : extra ? 'also in' : undefined}
                    onRemove={mayManage && extra ? () => removeFromTeam(extra.id) : undefined}
                    removeTitle={`Remove ${fullName(p)} from ${title}`} />
                );
              })}
            </div>
          )}
          {type === 'team' && (
            <div className="mt-2 text-[8px] text-gray-400">
              A person's primary team is set on their record (People → person → Work). Members added here are additional, and reach this team's pages as well as their own team's.
            </div>
          )}
          {mayManage && candidates.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <select value={addMember} onChange={e => setAddMember(e.target.value)} className="text-[9px] border border-gray-300 rounded px-2 py-1 bg-white">
                <option value="">Add a member…</option>
                {candidates.map(p => <option key={p.id} value={p.id}>{fullName(p)}</option>)}
              </select>
              <button onClick={() => addToTeam(key, addMember)} disabled={!addMember}
                className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50">Add</button>
            </div>
          )}
        </div>

        {pageChecklist(detail)}
        {previewModal()}
      </div>
    );
  }

  // One person: everything they reach, and why.
  if (tab === 'people' && personId) {
    const person = people.find(p => p.id === personId);
    if (!person) { setPersonId(null); return null; }
    const ps = principalsOf(person);
    const personPrincipal = pkey('person', person.id);
    const isAdminPerson = ps.includes('role:admin');
    const reach = (page) => canAccessWith(ps, page, pending);
    const rows = pageRows();
    return (
      <div className="fade-in lg:col-span-3" data-tour="person-view">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button onClick={() => setPersonId(null)} className="text-[9px] text-blue-600 underline">← Back to people</button>
          <h2 className="text-base font-extrabold text-navy">{fullName(person)}</h2>
          <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold">{person.team_id ? teamName(person.team_id) : 'No team'}</span>
          {(person.extra_memberships || []).map(m => <span key={m.id} className="text-[8px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">also {teamName(m.team_id)}</span>)}
          {leadsOf.has(person.id) && <span className="text-[8px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-semibold">Team Lead</span>}
          {saveControls(personPrincipal)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
          <div className="bg-white border border-gray-200 rounded p-3">
            <div className="text-[8px] font-bold text-gray-500 uppercase tracking-wide mb-2">Roles</div>
            {!canEdit && <div className="text-[8px] text-gray-400 mb-1">Only an administrator changes roles.</div>}
            <div className="grid gap-1">
              {ASSIGNABLE.map(role => {
                const on = parseRoles(person.user_role).includes(role);
                return (
                  <label key={role} className={`flex items-center gap-2 text-[9px] px-1 py-0.5 rounded ${canEdit ? 'cursor-pointer hover:bg-gray-50' : ''}`}>
                    <input type="checkbox" checked={on} disabled={!canEdit || roleBusy} onChange={() => toggleRole(person, role)} className="accent-gold" />
                    <span className={on ? 'text-gray-800 font-medium' : 'text-gray-500'}>{ROLE_LABELS[role] || role}</span>
                  </label>
                );
              })}
            </div>
            <div className="mt-2 text-[8px] text-gray-400">Team membership is edited on the Teams tab, and the primary team on the person's record.</div>
          </div>
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded overflow-hidden">
            <table className="w-full" style={{ fontSize: 8 }}>
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-3 py-2 font-bold text-gray-600 uppercase tracking-wide border-b border-gray-200">Page</th>
                  <th className="px-2 py-2 font-bold text-gray-500 uppercase border-b border-gray-200 text-center">Access</th>
                  <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase border-b border-gray-200">Because</th>
                  {canEdit && <th className="px-2 py-2 font-bold text-gray-500 uppercase border-b border-gray-200 text-center whitespace-nowrap">Direct grant</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(([mod, pages]) => (
                  <Fragment key={mod}>
                    <tr className="bg-gray-50">
                      <td colSpan={canEdit ? 4 : 3} className="px-3 py-1 text-[7px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100">{MODULE_LABELS[mod] || mod}</td>
                    </tr>
                    {pages.map(page => {
                      const has = reach(page);
                      const reasons = has ? reasonsFor(person, page) : [];
                      return (
                        <tr key={page} className="border-b border-gray-50 hover:bg-blue-50/40">
                          <td className={`px-3 py-1 text-gray-700 font-medium ${isAction(page) ? 'pl-7 text-gray-500' : ''}`}>{isAction(page) ? `↳ ${labelOf(page)}` : labelOf(page)}</td>
                          <td className="px-2 py-1 text-center">
                            <span className={`inline-block w-3 h-3 rounded-sm border ${has ? 'bg-navy border-navy' : 'border-gray-300'}`} aria-label={has ? 'held' : 'not held'} />
                          </td>
                          <td className="px-3 py-1 text-gray-500">{has ? reasons.join(', ') : (isAdminPerson ? '' : 'no grant')}</td>
                          {canEdit && <td className="px-2 py-1 text-center">{checkbox(personPrincipal, page)}</td>}
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {previewModal()}
      </div>
    );
  }

  // The tabs.
  const tabs = [
    { id: 'teams', label: 'Teams' },
    ...(canEdit ? [{ id: 'roles', label: 'Roles' }, { id: 'people', label: 'People' }] : []),
  ];
  const matchesSearch = (p) => !personSearch || fullName(p).toLowerCase().includes(personSearch.toLowerCase());

  return (
    <div className="fade-in lg:col-span-3">
      {header('Access')}
      <div className="text-[9px] text-gray-500 -mt-3 mb-3">
        {canEdit
          ? 'Who reaches which page. Teams carry the bulk of access, roles supplement it, and a person can be given a page directly. A tick is access; removing one shows who would lose it first.'
          : 'Who is in each team. Adding somebody to a team gives them that team\'s pages as well as their own team\'s.'}
      </div>

      <div data-tour="tabs" className="flex rounded-lg border border-gray-200 overflow-hidden w-fit mb-3">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setPersonId(null); }}
            className={`px-3 py-1 text-[9px] font-semibold ${tab === t.id ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'teams' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 mb-4">
            {teams.map(t => {
              const principal = pkey('team', t.id);
              const n = (pending[principal] || new Set()).size;
              return (
                <button key={t.id} onClick={() => setDetail(principal)}
                  className={`bg-white border rounded p-2.5 text-left hover:border-blue-400 hover:shadow-sm transition-all ${dirty(principal) ? 'border-amber-300' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: t.color || '#A1723D' }} />
                    <div className="text-[10px] font-bold text-navy truncate">{t.name}</div>
                  </div>
                  <div className="text-[8px] text-gray-400 mt-0.5">{t.lead_name ? `Lead: ${t.lead_name}` : 'No lead set'}</div>
                  <div className="text-[9px] text-gray-600 mt-1">{membersOf(t.id).length} {membersOf(t.id).length === 1 ? 'member' : 'members'} · {n} {n === 1 ? 'page' : 'pages'}</div>
                  {canManageTeam(t.id) && !canEdit && <div className="text-[7px] text-emerald-600 font-semibold mt-0.5">You manage membership</div>}
                  {dirty(principal) && <div className="text-[7px] text-amber-500 font-semibold mt-0.5">Unsaved</div>}
                </button>
              );
            })}
            {teams.length === 0 && <div className="text-[9px] text-gray-500">No active teams. Teams are created under Admin → Settings → Teams.</div>}
          </div>
          {teams.length > 0 && matrix('team', teams.map(t => ({ key: t.id, label: t.name })))}
        </>
      )}

      {tab === 'roles' && canEdit && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 mb-4">
            {ROLE_KEYS.map(role => {
              const principal = pkey('role', role);
              const n = (pending[principal] || new Set()).size;
              const count = holdersOf(role).length;
              return (
                <button key={role} onClick={() => setDetail(principal)}
                  className={`bg-white border rounded p-2.5 text-left hover:border-blue-400 hover:shadow-sm transition-all ${dirty(principal) ? 'border-amber-300' : 'border-gray-200'}`}>
                  <div className="text-[10px] font-bold text-navy">{ROLE_LABELS[role]}</div>
                  <div className="text-[8px] text-gray-400 font-mono mt-0.5">{role}</div>
                  <div className="text-[9px] text-gray-600 mt-1">{role === 'admin' ? 'every page' : `${n} ${n === 1 ? 'page' : 'pages'}`} · {count} {count === 1 ? 'person' : 'people'}</div>
                  {dirty(principal) && <div className="text-[7px] text-amber-500 font-semibold mt-0.5">Unsaved</div>}
                </button>
              );
            })}
          </div>
          {matrix('role', ROLE_KEYS.map(r => ({ key: r, label: ROLE_LABELS[r] })))}
          <div className="mt-2 text-[8px] text-gray-400">Administrators hold every page whatever is ticked here. Team Lead is derived from each team's lead and cannot be assigned.</div>
        </>
      )}

      {tab === 'people' && canEdit && (
        <div className="bg-white border border-gray-200 rounded p-3">
          <input value={personSearch} onChange={e => setPersonSearch(e.target.value)} placeholder="Find a person…"
            className="text-[9px] border border-gray-300 rounded px-2 py-1 w-64 mb-3" />
          <table className="w-full" style={{ fontSize: 9 }}>
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-2 py-1.5 font-bold text-gray-600 uppercase text-[8px]">Person</th>
                <th className="px-2 py-1.5 font-bold text-gray-600 uppercase text-[8px]">Team</th>
                <th className="px-2 py-1.5 font-bold text-gray-600 uppercase text-[8px]">Roles</th>
                <th className="px-2 py-1.5 font-bold text-gray-600 uppercase text-[8px] text-right">Direct grants</th>
              </tr>
            </thead>
            <tbody>
              {people.filter(matchesSearch).map(p => (
                <tr key={p.id} onClick={() => setPersonId(p.id)} className="border-b border-gray-50 hover:bg-blue-50/40 cursor-pointer">
                  <td className="px-2 py-1"><AvatarChip person={p} /></td>
                  <td className="px-2 py-1 text-gray-600">{p.team_id ? teamName(p.team_id) : <span className="text-amber-600">none</span>}{(p.extra_memberships || []).length ? ` +${p.extra_memberships.length}` : ''}</td>
                  <td className="px-2 py-1 text-gray-600">{[...parseRoles(p.user_role), ...(leadsOf.has(p.id) ? ['team-lead'] : [])].map(r => ROLE_LABELS[r] || r).join(', ') || <span className="text-amber-600">none</span>}</td>
                  <td className="px-2 py-1 text-right text-gray-600">{(pending[pkey('person', p.id)] || new Set()).size || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {previewModal()}
      {tour.open && <FeatureTour steps={accessTourSteps} context={{ canEdit }} onFinish={tour.finish} onSkip={tour.skip} />}
    </div>
  );
}
